import axios from 'axios'
import QueryParamsUtil from 'api/utils/query-params-util'

const API_PATH = '/api/v2/admins/outbounds/internal_requests'

const OutboundInternalRequestsApiV2 = {

  /**
   * Get a list of internal requests
   * Invoke `GET api/v2/admins/outbounds/internal-requests'
   * @param {object} tableState - ReactTable final state
   */
  index: async ({ tableState, additionalFilters }) => {
    const params = QueryParamsUtil.format(tableState, additionalFilters)
    const path   = API_PATH

    const response = axios.get(path, { params })
    return response
  },

  /**
   * Get a  internal request object
   * Invoke `GET api/v2/admins/outbounds/internal-requests/:id'
   * @param {number} id - Internal Request id
   */
  show: async (id) => {
    const path     = `${API_PATH}/${id}`
    const response = axios.get(path)
    return response
  },

  /**
   * Get an items of internal request
   * Invoke `GET api/v2/admins/outbounds/internal-requests/:internal_request_id/items'
   * @param {number} internal_request_id - Internal Request ID
   * @param {object} tableState - ReactTable final state
   * @param {object} additionalFilters - Additional filter. Use this param to overwrite/filtering outside value from react-table
   */
  getItems: async ({ internal_request_id, tableState, additionalFilters }) => {
    const params = QueryParamsUtil.format(tableState, additionalFilters)
    const path   = `${API_PATH}/${internal_request_id}/items`

    const response = axios.get(path, { params })
    return response
  },

  /**
   * Cancel an internal request
   * Invoke `PATCH api/v2/admins/outbounds/internal-requests/:internal_request_id/cancel'
   * @param {number} internal_request_id - Internal Request ID
   */
  cancel: async ( internal_request_id ) => {
    const path = `${API_PATH}/${internal_request_id}/cancel`

    const response = axios.patch(path)
    return response
  },

  /**
   * Receive all items of internal request
   * Invoke `PATCH api/v2/admins/outbounds/internal-requests/:internal_request_id/receive_products'
   * @param {number} internal_request_id - Internal Request ID
   */
  receiveAll: async ( internal_request_id ) => {
    const path = `${API_PATH}/${internal_request_id}/receive_products`

    const response = axios.patch(path)
    return response
  },

  /**
   * Create a new internal request
   * Invoke `POST api/v2/admins/customers/carts/rules'
   * @param {object} payload - Payload data to be send to backend
   */
  create: async (values) => {
    const path    = API_PATH
    const payload = {
      internal_request: {
        purpose        : values.purpose,
        requested_date : values.requested_date,
        returned_date  : values.returned_date,
        preorder_date  : values.preorder_date,
        items          : values.selected_inventories.map((value) => ({
          inventory_id : value.id,
          quantity     : value.quantity
        }))
      }
    }

    const response = axios.post(path, payload)
    return response
  },

  /**
   * Pick item from an internal request
   * Invoke `PATCH api/v2/admins/outbounds/internal-requests/:internal_request_id/items/:item_id/pick`
   * @param {number} internal_request_id - Id of the internal request
   * @param {number} item_id - Id of the internal request item
   * @param {number} location_id - Id of the location of the item
   */
  pickItem: async ({
    internal_request_id,
    item_id,
    location_id,
    quantity
  }) => {
    const endpoint = `${API_PATH}/${internal_request_id}/items/${item_id}/pick`
    const payload  = {
      'item': {
        'tl_warehouse_location_id' : location_id,
        'quantity'                 : quantity
      }
    }

    const response = axios.patch(endpoint, payload)
    return response
  },
  /**
   * Return all selected items of internal request
   * Invoke `PATCH api/v2/admins/outbounds/internal-requests/:internal_request_id/items/bulk_return'
   * @param {number} internal_request_id - Internal Request ID
   * @param {Array} items - Array of Item
   */
  bulkReturn: async ( internal_request_id, items ) => {
    const path = `${API_PATH}/${internal_request_id}/items/bulk_return`
    const body = {
      items: items
    }

    const response = axios.patch(path, body)
    return response
  },

  /**
   * Pick item from an internal request
   * Invoke `PATCH api/v2/admins/outbounds/internal-requests/:internal_request_id/items/:item_id/pick`
   * @param {number} internal_request_id - Id of the internal request
   * @param {number} item_id - Id of the internal request item
   * @param {string} reject_reason - Reason to reject the item
   * @param {string} reject_note - Additional reject note
   */
  rejectItem: async ({
    internal_request_id,
    item_id,
    quantity,
    reject_reason,
    reject_note
  }) => {
    const endpoint = `${API_PATH}/${internal_request_id}/items/${item_id}/reject`
    const payload  = {
      'item': {
        'quantity'      : quantity,
        'reject_reason' : reject_reason,
        'reject_note'   : reject_note
      }
    }

    const response = axios.post(endpoint, payload)
    return response
  },

  /**
   * Send products of an Internal Request Item
   * Invoke `PATCH api/v2/admins/outbounds/internal-requests/:internal_request_id/send_products
   * @param {number} internal_request_id - Id of the internal request
   * @param {array} items - Array of picked items
   */
  sendProducts: async ({
    internal_request_id,
    items
  }) => {
    const endpoint = `${API_PATH}/${internal_request_id}/send_products`
    const payload  = {
      'items': items
    }

    const response = axios.patch(endpoint, payload)
    return response
  },
  /**
   * Keep an item of internal request
   * Invoke `PATCH api/v2/admins/outbounds/internal-requests/:internal_request_id/items/:item_id/keep'
   * @param {number} internal_request_id - Internal Request ID
   * @param {number} item_id - Item ID
   * @param {number} quantity - Item Quantity
   */
  keep: async ( internal_request_id, item_id, quantity ) => {
    const path = `${API_PATH}/${internal_request_id}/items/${item_id}/keep`
    const body = {
      item: {
        quantity: quantity
      }
    }

    const response = axios.patch(path, body)
    return response
  },

  /**
   * Return an internal request item
   * Invoke `PATCH api/v2/admins/outbounds/internal-requests/:internal_request_id/items/returned`
   * @param {number} internal_request_id - Id of the internal request
   * @param {string} sku - internal request item inventory sku
   * @param {number} quantity - returned item quantity
   */
  returnItem: async ({
    internal_request_id,
    sku,
    quantity
  }) => {
    const endpoint = `${API_PATH}/${internal_request_id}/items/returned`
    const payload  = {
      'item': {
        'sku'      : sku,
        'quantity' : quantity
      }
    }

    const response = axios.patch(endpoint, payload)
    return response
  },

  /**
   * Put away an internal request item
   * Invoke `PATCH api/v2/admins/outbounds/internal-requests/:internal_request_id/items/returned`
   * @param {number} internal_request_id - Id of the internal request
   * @param {string} sku - internal request item inventory sku
   * @param {number} quantity - returned item quantity
   */
  putAwayItem: async ({
    internal_request_id,
    sku,
    quantity,
    location,
    sublocation
  }) => {
    const endpoint = `${API_PATH}/${internal_request_id}/items/put_away`
    const payload  = {
      'item': {
        'inventory_sku' : sku,
        'quantity'      : quantity,
        'location'      : location,
        'sublocation'   : sublocation
      }
    }

    const response = axios.patch(endpoint, payload)
    return response
  }
}

export default OutboundInternalRequestsApiV2
